import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, allComics } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const comics = allComics.filter((c) => c.category === category?.name);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900">分类不存在</h1>
            <Link to="/" className="text-primary-500 mt-4 inline-block cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageTitle = `${category.name}韩漫 - 最新${category.name}类韩国漫画在线阅读 - 韩漫之家`;
  const pageDesc = `韩漫之家提供最新高清${category.name}类韩国漫画在线阅读，${category.description}，每日更新，免费阅读。`;

  return (
    <>
      <Navbar />
      <main>
        <div className="hidden">
          <meta data-page-title={pageTitle} />
        </div>

        <section className="relative h-[280px] md:h-[360px] w-full overflow-hidden mt-0">
          <div className="absolute inset-0">
            <img
              src={category.image}
              alt={category.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/55"></div>
          <div className="relative z-10 h-full flex items-center px-4 md:px-6">
            <div className="max-w-7xl mx-auto w-full">
              <nav className="flex items-center gap-2 text-white/80 text-xs mb-4" aria-label="面包屑导航">
                <Link to="/" className="hover:text-white transition-colors cursor-pointer">首页</Link>
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line"></i>
                </span>
                <span className="text-white">漫画分类</span>
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line"></i>
                </span>
                <span className="text-white font-medium">{category.name}</span>
              </nav>
              <div className="flex items-center gap-3 mb-2">
                <span className="w-10 h-10 flex items-center justify-center bg-primary-500/90 rounded-full">
                  <i className={`${category.icon} text-lg text-white`}></i>
                </span>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-white">{category.name}类韩漫</h1>
              </div>
              <p className="text-white/70 text-sm max-w-lg">
                {category.description}，共收录 <strong className="text-white">{category.count}</strong> 部作品
              </p>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-foreground-900">全部{category.name}漫画</h2>
                <div className="flex items-center gap-2 text-xs text-foreground-500">
                  <span>共 {comics.length} 部</span>
                </div>
              </div>

              {comics.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop>
                  {comics.map((comic) => (
                    <Link
                      key={comic.id}
                      to={`/comic/${comic.slug}`}
                      className="group relative rounded-lg overflow-hidden cursor-pointer flex flex-col bg-background-100 hover:bg-background-200/70 transition-colors"
                    >
                      <div className="relative aspect-[5/7] overflow-hidden">
                        <img src={comic.coverImage} alt={comic.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        <span className="absolute top-3 right-3 text-xs font-medium px-2 py-1 rounded-full bg-primary-500/90 text-white whitespace-nowrap">
                          {comic.status}
                        </span>
                      </div>
                      <div className="p-4 flex flex-col gap-2">
                        <h3 className="font-heading text-base font-bold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                          {comic.title}
                        </h3>
                        <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">{comic.description}</p>
                        <div className="flex items-center gap-2 text-xs text-foreground-400 mt-auto pt-2">
                          <span className="flex items-center gap-0.5">
                            <span className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                            </span>
                            {comic.rating}
                          </span>
                          <span>·</span>
                          <span>{comic.author}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <p className="text-foreground-500">该分类暂无漫画作品</p>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-lg font-bold text-foreground-900 mb-5">其他分类</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {categories.filter((c) => c.slug !== slug).map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background-50 hover:bg-primary-50 transition-colors cursor-pointer"
                  >
                    <span className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-full">
                      <i className={`${cat.icon} text-secondary-600`}></i>
                    </span>
                    <span className="text-sm font-medium text-foreground-700">{cat.name}</span>
                    <span className="text-xs text-foreground-400">{cat.count}部</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: pageTitle,
            description: pageDesc,
            url: `http://guanlist.com/category/${slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '韩漫之家',
              url: 'http://guanlist.com/',
            },
            about: {
              '@type': 'Thing',
              name: category.name,
              description: category.description,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'http://guanlist.com/' },
              { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'http://guanlist.com/category/action' },
              { '@type': 'ListItem', position: 3, name: category.name },
            ],
          }),
        }}
      />
    </>
  );
}